// AnalyticsTracker.jsx
import { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { logEvent, setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { analytics } from '../../firebase';
import { Box, Button, Text, Link, useToast } from '@chakra-ui/react';
import { useLegalContent } from '../../hooks/useLegalContent';

/** --------------------------------------------------
 *  1. Helpers
 * ------------------------------------------------- */
const getPageTitleFromPath = pathname => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'Accueil';

  if (segments[0] === 'agence') return 'Agence';
  if (segments[0] === 'communications') {
    if (segments[1] === 'offres') return segments[2] ? '' : 'Offres spéciales';
    if (segments[1] === 'blog') return 'Blog Communications';
    if (segments[1] === 'carnet-de-voyage') return 'Carnet de Voyages';
    return 'Communications';
  }
  if (segments[0] === 'nos-produits') {
    if (segments[1] === 'type') {
      if (segments[2] === 'allinone') return 'Nos Produits: All In One';
      if (segments[2] === 'coups-de-coeur')
        return 'Nos Produits: Coups de Coeur';
      return segments[2]
        ? `Nos Produits: Type ${segments[2]}`
        : 'Nos Produits: Types';
    }
    if (segments[1] === 'details' && segments[2]) return '';
    return 'Nos Produits';
  }
  if (segments[0] === 'assurances') return 'Assurances';
  if (segments[0] === 'destinations') return 'Destinations';
  if (segments[0] === 'login') return 'Login';
  if (segments[0] === 'newsletter') return 'Newsletter';
  if (segments[0] === 'contact') return 'Contact';
  if (segments[0] === 'cookies') return 'Cookies';
  if (segments[0] === 'cgv') return 'Conditions Générales de Vente';
  if (segments[0] === 'mentions-legales') return 'Mentions Légales';
  if (segments[0] === 'protection-des-donnees') return 'Protection des Données';
  if (segments[0] === 'administration') return 'Administration';

  return pathname; // fallback
};

/** --------------------------------------------------
 *  2. Component
 * ------------------------------------------------- */
const AnalyticsTracker = () => {
  const location = useLocation();
  const [hasConsented, setHasConsented] = useState(null); // null = pas encore choisi
  const firstPageSent = useRef(false); // évite un double hit post-accept
  const storedConsent = localStorage.getItem('analytics_consent');
  const toast = useToast();
  const shouldFetchBanner = hasConsented === false && storedConsent === null;
  const { content } = useLegalContent('legal', { fallbackToFrench: false, enabled: shouldFetchBanner });

  /* ---------- Mount : lire le consentement stocké ---------- */
  useEffect(() => {
    if (storedConsent === 'true') {
      setHasConsented(true);
      setAnalyticsCollectionEnabled(analytics, true);
    } else if (storedConsent === 'false') {
      setHasConsented(false);
      setAnalyticsCollectionEnabled(analytics, false);
    } else {
      setHasConsented(false); // pas encore décidé → on montre le bandeau
    }
  }, [storedConsent]);

  /* ---------- Fonction utilitaire : ping GA4 + Firebase ---------- */
  const sendPageView = path => {
    const pageTitle = getPageTitleFromPath(path);

    /* GA4 (gtag) */
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: pageTitle || document.title,
        page_location: window.location.href,
        page_path: path,
      });
    }

    /* Firebase Analytics */
    if (hasConsented) {
      logEvent(analytics, 'page_view', {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: path,
      });
    }
  };

  /* ---------- Sur chaque changement de route (après consentement) ---------- */
  useEffect(() => {
    if (!hasConsented) return; // pas de consentement → rien

    // Évite d’envoyer 2 fois la vue de la page où l’utilisateur clique “Accepter”
    if (!firstPageSent.current) {
      firstPageSent.current = true;
    } else {
      sendPageView(location.pathname);
    }
  }, [location, hasConsented]);

  /* ---------- Met à jour les 4 signaux Consent Mode v2 ---------- */
  const updateGtagConsent = granted => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: granted ? 'granted' : 'denied',
        analytics_storage: granted ? 'granted' : 'denied',
        ad_user_data: granted ? 'granted' : 'denied',
        ad_personalization: granted ? 'granted' : 'denied',
      });
    }
  };

  /* ---------- Handlers bouton ---------- */
  const handleAccept = () => {
    localStorage.setItem('analytics_consent', 'true');
    setAnalyticsCollectionEnabled(analytics, true);
    setHasConsented(true);
    updateGtagConsent(true);
    sendPageView(location.pathname); // première vue après accord
    if (content?.cookies_toast_title || content?.cookies_toast_enabled_desc) {
      toast({
        title: content?.cookies_toast_title || 'Consentement mis à jour.',
        description: content?.cookies_toast_enabled_desc || '',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleReject = () => {
    localStorage.setItem('analytics_consent', 'false');
    setAnalyticsCollectionEnabled(analytics, false);
    setHasConsented(false);
    updateGtagConsent(false);
    if (content?.cookies_toast_title || content?.cookies_toast_disabled_desc) {
      toast({
        title: content?.cookies_toast_title || 'Consentement mis à jour.',
        description: content?.cookies_toast_disabled_desc || '',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /* ---------- Bandeau cookies (affiché si aucun choix encore fait) ---------- */
  const shouldShowBanner = hasConsented === false && storedConsent === null;

  return shouldShowBanner ? (
    <Box
      position="fixed"
      bottom="0"
      left="0"
      right="0"
      bg="white"
      borderTop="1px solid #ccc"
      p={4}
      zIndex={1000}
      display="flex"
      flexDirection={{ base: 'column', md: 'row' }}
      alignItems={{ base: 'flex-start', md: 'center' }}
      justifyContent="space-between"
    >
      <Text
        fontFamily="Raleway"
        fontSize={{ base: 'sm', md: 'md' }}
        mb={{ base: 4, md: 0 }}
        mr={{ base: 0, md: 4 }}
        textAlign="justify"
      >
        {(content?.cookies_banner_p1_before_link || 'Nous utilisons des cookies afin de mesurer la fréquentation et l’utilisation de nos contenus de manière entièrement anonymisée, dans le but d\'améliorer votre expérience. Aucune donnée permettant de vous identifier personnellement n’est enregistrée. Vous pouvez en savoir plus dans notre')}{' '}
        <Link href="/cookies" color="teal.500">
          {content?.cookies_banner_policy_link_label || 'Politique en matière de cookies'}
        </Link>
        {content?.cookies_banner_p1_after_link || ". Acceptez-vous l'utilisation de ces cookies d'analyse anonymes ?"}
      </Text>

      <Box display="flex" flexWrap={{ base: 'wrap', md: 'nowrap' }}>
        <Button
          variant="outline"
          color="primaryOrange.400"
          border="2px solid"
          bg="#fffbf7"
          _hover={{ bg: 'primaryCreme.400' }}
          fontSize={{ base: 'md', md: 'lg' }}
          p={{ base: 4, md: 6 }}
          mr={2}
          fontFamily="Raleway"
          fontWeight="400"
          onClick={handleAccept}
          mb={{ base: 2, md: 0 }}
        >
          {content?.cookies_choice_accept_label || 'Accepter'}
        </Button>
        <Button
          variant="outline"
          border="2px solid"
          fontSize={{ base: 'md', md: 'lg' }}
          p={{ base: 4, md: 6 }}
          fontFamily="Raleway"
          fontWeight="400"
          onClick={handleReject}
        >
          {content?.cookies_choice_refuse_label || 'Refuser'}
        </Button>
      </Box>
    </Box>
  ) : null;
};

export default AnalyticsTracker;
